import { IUpdatedAssociations } from "@Totum/QuestionnaireBuilder/Domain/types";
import { ITreeItem } from "@Totum/Main/Utils";
import { IOperationResult } from "./types";

/**
 * Find the parent item in the tree
 * @param data The tree data
 * @param targetValue The value to search for
 */
const findParentItemInTree = (data: ITreeItem[], targetValue: string): ITreeItem | null => {
    // Helper function to search the tree
    const search = (items: ITreeItem[], parent: ITreeItem | null = null): ITreeItem | null => {
        for (const item of items) {
            if (item.value === targetValue) {
                return parent ?? null;
            }
            if (item.children) {
                const result = search(item.children, item);
                if (result !== null) return result;
            }
        }
        return null;
    };

    // Start the search from the top level
    return search(data);
};

/**
 * Process the selected items and return the updated associations
 * @param valueWithPrefix the value of the item with the prefix
 * @param item an individual tree item
 * @param data the tree data
 * @param currentAssociations the currently associated items
 */
export const processTreeItemAddition = (
    valueWithPrefix: string,
    item: ITreeItem,
    data: ITreeItem[],
    currentAssociations: string[]
): IUpdatedAssociations => {
    const itemsToRemove = {
        activitySet: new Set<string>(),
        categorySet: new Set<string>(),
    };

    const itemsToAdd = {
        activitySet: new Set<string>(),
        categorySet: new Set<string>(),
    };

    const process = (valueToProcess: string, itemToProcess: ITreeItem) => {
        const isCatOrSubCat = valueToProcess.includes("cat-");

        // Recursively remove all activities because we only need to store categories
        const removeNestedActivities = (children: ITreeItem[]) => {
            children.forEach((child) => {
                if (child.value.includes("cat-")) {
                    removeNestedActivities(child.children ?? []);
                } else {
                    itemsToRemove.activitySet.add(child.value);
                }
            });
        };

        if (isCatOrSubCat) {
            removeNestedActivities(item.children ?? []);
            itemsToAdd.categorySet.add(valueToProcess.split("-")[1]);
            // now we will add all nested categories
            return item.children?.forEach((child) => {
                if (child.value.includes("cat-")) {
                    itemsToAdd.categorySet.add(child.value.split("-")[1]);
                }
            });
        }

        // before we add this item we want to see if its siblings are already selected
        const parentItem = findParentItemInTree(data, valueWithPrefix);
        // find parent key
        const parentKey = parentItem?.key ? parentItem?.key.split("-")[1] : null;

        if (!parentKey) {
            // if there is no parent, add the activity
            itemsToAdd.activitySet.add(valueToProcess);
            return;
        }

        // then we check if the siblings are all in the list
        const allInList = parentItem?.children
            ?.filter((child) => child.key !== valueWithPrefix)
            .every((child) => currentAssociations.includes(child.value));

        if (allInList) {
            itemsToAdd.categorySet.add(parentKey);
        } else {
            itemsToAdd.activitySet.add(valueToProcess);
        }
    };
    process(valueWithPrefix, item);

    const updatedAssociations = {
        activitiesToAdd: Array.from(itemsToAdd.activitySet),
        categoriesToAdd: Array.from(itemsToAdd.categorySet),
        activitiesToRemove: Array.from(itemsToRemove.activitySet),
        categoriesToRemove: Array.from(itemsToRemove.categorySet),
    };

    return updatedAssociations;
};

/**
 * Process the deselected items and return the updated associations
 * @param valueWithPrefix the value of the item with the prefix
 * @param item an individual tree item
 * @param data the tree data
 * @param currentAssociations the currently associated items
 */
export const processTreeItemRemoval = (
    valueWithPrefix: string,
    item: ITreeItem,
    data: ITreeItem[],
    currentAssociations: string[]
): IUpdatedAssociations => {
    const itemsToRemove = {
        activitySet: new Set<string>(),
        categorySet: new Set<string>(),
    };

    const itemsToAdd = {
        activitySet: new Set<string>(),
        categorySet: new Set<string>(),
    };

    const process = (valueToProcess: string, itemToProcess: ITreeItem, isNested = false) => {
        const isCatOrSubCat = valueToProcess.includes("cat-");

        if (isCatOrSubCat && itemToProcess.children) {
            const value = extractValueFromKey(valueToProcess);
            itemsToRemove.categorySet.add(value);
            // we need to remove child items as well
            itemToProcess.children.forEach((child) => {
                process(child.value, child, true);
            });
        } else {
            // remove the activity item
            itemsToRemove.activitySet.add(valueToProcess);
        }

        // find parent key
        const parentItem = findParentItemInTree(data, valueToProcess);
        const parentKey = parentItem?.key ? extractValueFromKey(parentItem.key) : null;

        // If the parent was previously selected, remove the parent
        if (parentKey && currentAssociations.includes(parentKey) && !isNested) {
            itemsToRemove.categorySet.add(extractValueFromKey(parentKey));
            // then add the remaining siblings
            parentItem?.children
                ?.filter((child) => child.key !== valueToProcess)
                .forEach((child) => {
                    if (child.value.includes("cat-")) {
                        itemsToAdd.categorySet.add(extractValueFromKey(child.value));
                    } else {
                        itemsToAdd.activitySet.add(child.value);
                    }
                });
        }
    };
    process(valueWithPrefix, item);

    const updatedAssociations = {
        activitiesToAdd: Array.from(itemsToAdd.activitySet),
        categoriesToAdd: Array.from(itemsToAdd.categorySet),
        activitiesToRemove: Array.from(itemsToRemove.activitySet),
        categoriesToRemove: Array.from(itemsToRemove.categorySet),
    };

    return updatedAssociations;
};

/**
 * Extract the value from the item key
 */
export const extractValueFromKey = (key: string): string => (key.includes("-") ? key.split("-")[1] : key);

/**
 * Creates an operation result object.
 * @param result - If true: success, if false: failure, if undefined: no action.
 * @returns Object with isSuccess, isFailure (if action taken), and hasAction flags.
 * @example
 * createOperationResult(true)   // { isSuccess: true, isFailure: false, hasAction: true }
 * createOperationResult(false)  // { isSuccess: false, isFailure: true, hasAction: true }
 * createOperationResult()       // { hasAction: false }
 */
export const createOperationResult = (result?: boolean): IOperationResult =>
    typeof result === "boolean"
        ? {
              isSuccess: result,
              isFailure: !result,
              hasAction: true,
          }
        : {
              hasAction: false,
          };
